import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AnalysisReportService, QuestionReport } from '../services/analysis-report.service';

type SortColumn = 'id' | 'name' | 'confidenceScore';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-analysis-report-questions',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './analysis-report-questions.component.html',
  styleUrl: './analysis-report-questions.component.css',
  standalone: true
})
export class AnalysisReportQuestionsComponent implements OnInit, AfterViewInit, OnDestroy {
  currentTheme: 'light' | 'dark' = 'light';
  isLoading: boolean = false;
  templateId: string = '';
  templateName: string = '';
  questions: QuestionReport[] = [];
  searchQuery: string = '';
  sortColumn: SortColumn = 'id';
  sortDirection: SortDirection = 'asc';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private analysisReportService: AnalysisReportService
  ) {}

  ngOnInit() {
    this.initializeTheme();

    // Get templateId from navigation state (internal SPA navigation)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || history.state;

    if (state?.templateId) {
      this.templateId = state.templateId;
      this.templateName = state.templateName || '';
      this.loadQuestions();
      return;
    }

    // Fallback to query params for direct/external links (e.g. from creator UI)
    this.route.queryParams.subscribe(params => {
      const templateId = params['templateId'] || params['templateid'];
      if (templateId) {
        this.templateId = templateId;
        this.templateName = params['templateName'] || '';
        this.loadQuestions();
      } else {
        // Redirect back if no templateId provided
        this.router.navigate(['/analysis-report']);
      }
    });
  }

  ngAfterViewInit() {
    this.initializeLucideIcons();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadQuestions() {
    this.isLoading = true;
    this.analysisReportService.getQuestions(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.templateName = data.templateName;
          this.questions = data.questions;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load questions:', err);
          this.isLoading = false;
          this.templateName = '';
          this.questions = [];
        }
      });
  }

  get filteredQuestions(): QuestionReport[] {
    let result = this.questions;
    
    // Filter by search query
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(q => 
        q.name.toLowerCase().includes(query) ||
        q.id.toString().includes(query) ||
        (q.prompts && q.prompts.toLowerCase().includes(query))
      );
    }
    
    // Sort
    result = [...result].sort((a, b) => {
      let comparison = 0;
      if (this.sortColumn === 'id') {
        comparison = a.id.localeCompare(b.id, undefined, { numeric: true });
      } else if (this.sortColumn === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (this.sortColumn === 'confidenceScore') {
        comparison = a.confidenceScore - b.confidenceScore;
      }
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }

  toggleSort(column: SortColumn) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  viewQuestionDetails(question: QuestionReport) {
    this.router.navigate(['/analysis-report-questions-details'], {
      state: { 
        templateId: this.templateId,
        templateName: this.templateName,
        questionId: question.id,
        questionName: question.name,
        questionPrompts: question.prompts
      }
    });
  }

  goToDocumentation() {
    this.router.navigate(['/documentation']);
  }

  formatScore(score: number): string {
    return score.toFixed(1);
  }

  getScoreColor(score: number): string {
    if (score >= 85) return '#10b981';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  }

  getDonutStyle(score: number): string {
    const color = this.getScoreColor(score);
    const percentage = Math.min(score, 100);
    return `conic-gradient(${color} ${percentage * 3.6}deg, #e5e7eb ${percentage * 3.6}deg)`;
  }

  getDonutStyleDark(score: number): string {
    const color = this.getScoreColor(score);
    const percentage = Math.min(score, 100);
    return `conic-gradient(${color} ${percentage * 3.6}deg, #374151 ${percentage * 3.6}deg)`;
  }

  getScoreBadgeClass(score: number): string {
    if (score >= 85) {
      return 'bg-emerald-500 text-white';
    } else if (score >= 70) {
      return 'bg-amber-500 text-white';
    } else {
      return 'bg-red-500 text-white';
    }
  }

  getConfidenceLabel(score: number): string {
    if (score >= 85) return 'High';
    if (score >= 70) return 'Medium';
    return 'Low';
  }

  goBack() {
    this.router.navigate(['/analysis-report']);
  }

  initializeTheme() {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    if (savedTheme) {
      this.currentTheme = savedTheme;
      this.applyTheme(savedTheme);
    }
  }

  setTheme(theme: 'light' | 'dark') {
    this.currentTheme = theme;
    this.applyTheme(theme);
    localStorage.setItem('theme', theme);
  }

  private applyTheme(theme: 'light' | 'dark') {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  private initializeLucideIcons() {
    if (typeof (window as any).lucide !== 'undefined') {
      (window as any).lucide.createIcons({
        attrs: {
          'stroke-width': 1.5
        }
      });
    }
  }

}
